<template>
  <a class="sticky top-0 z-20 md:hidden" href="#imt-search-modal">
    <div class="flex items-center px-4 py-5 mb-4 bg-white border-b-3 border-brand-600">
      <CorridorInfo
        class="justify-center"
        :country-from="countryFrom"
        :country-to="countryTo"
        :currency-from="currencyFrom"
        :currency-to="currencyTo"
        :amount="amount"
      />
      <svg role="presentation" focusable="false" tabindex="-1" class="icon icon-md ml-auto"   alt="edit" svg-inline  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16"><title>edit</title>
<path fill="#303030" d="M2 11.5V14h2.5l7.4-7.4-2.5-2.5L2 11.5Zm11.8-6.8c.3-.3.3-.7 0-1l-1.6-1.5a.7.7 0 0 0-.9 0l-1.2 1.2L12.6 6l1.2-1.2Z"/>
</svg>
    </div>
  </a>
  <div class="hidden md:block mx-auto max-w-container pt-box-2 pb-8">
    <MobileSearchForm
      v-if="showExperimentalLayout"
      class="box-container search-form overflow-visible py-6 xl:py-4 px-4 md:mx-auto md:grid md:gap-x-8 md:grid-cols-2 xl:flex max-w-6xl xl:max-w-none"
      :default-country-from="defaultCountryFrom"
      :default-country-to="defaultCountryTo"
      :default-currency-from="defaultCurrencyFrom"
      :default-currency-to="defaultCurrencyTo"
      :default-amount="defaultAmount"
      :payout="payout"
    />
    <form v-else class="box-container search-form" @submit.prevent="submitHandler">
      <div class="flex flex-col xl:flex-row">
        <div class="flex flex-row xl:flex-1">
          <CountrySelect
            class="flex-1 min-w-0 pt-6 px-6 pb-3 xl:pt-4 xl:pb-2 border-r-3 border-b-3 xl:border-b-0 border-gray-200"
            selection-class="py-3 pr-3"
            :label="$t('transfer_search.country_from')"
            :header-text="$t('transfer_search.sending_country')"
            v-model="countryFrom"
            @update:model-value="selectCountryFrom"
          />
          <CountrySelect
            class="flex-1 min-w-0 pt-6 px-6 pb-3 xl:pt-4 xl:pb-1 xl:border-b-0 border-b-3 xl:border-r-3 border-gray-200"
            selection-class="py-3 pr-3"
            :label="$t('transfer_search.country_to')"
            :header-text="$t('transfer_search.sending_currency')"
            v-model="countryTo"
            @update:model-value="selectCountryTo"
          />
        </div>
        <div class="flex flex-row p-6 xl:py-4 xl:flex-1">
          <div class="flex items-center flex-1 mr-4 xl:mr-6">
            <AmountInput
              class="mr-4 flex-1"
              label-class="xl:hidden"
              wrapper-class="border-brand-600"
              :currencies="fromCurrencies"
              :label="$t('transfer_search.you_send')"
              :header-text="$t('transfer_search.sending_currency')"
              :amount-value="amount"
              :amount-placeholder="$t('transfer_search.insert_amount')"
              :currency-value="currencyFrom"
              :custom-error="$t('transfer_search.amount_is_required')"
              required
              @input-amount="amount = $event"
              @input-currency="currencyFrom = $event"
            />
            <span
              class="hidden xl:inline ml-1 mr-5 text-14 font-bold opacity-80 uppercase text-gray-800"
            >
              {{ $t('transfer_search.to') }}
            </span>
            <CurrencySelect
              class="min-w-24"
              selection-class="py-3 pr-3"
              label-class="xl:hidden"
              :label="$t('transfer_search.to')"
              :header-text="$t('transfer_search.receiving_currency')"
              :currencies="toCurrencies"
              v-model="currencyTo"
              outlined
            />
          </div>
          <button
            type="submit"
            class="btn btn-amber text-17 md:text-19 items-center flex justify-center self-end h-16"
            :disabled="isLoading"
          >
            <span class="xl:hidden">{{ $t('compare') }}</span>
            <MSpinner
              v-if="isLoading"
              class="ml-2 xl:ml-0"
              size="h-6 w-6"
              stroke-width="3"
              color="text-white"
            />
            <svg role="presentation" focusable="false" tabindex="-1" v-else
              class="icon icon-md hidden xl:inline"
               
              alt="compare"
              svg-inline
             xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><title>compare</title>
  <path fill="currentColor" d="M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z"/>
</svg>
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script lang="ts" setup>
import type { Country } from '@core/plugins/master-data/types'
import { SearchSubmit } from '@core/analytics/core-events/moneyTransfer'

const props = defineProps<{
  defaultCountryFrom?: string
  defaultCountryTo?: string
  defaultCurrencyFrom?: string
  defaultCurrencyTo?: string
  defaultAmount?: number | null
  payout?: string
}>()

const { $masterData, $track, $features } = useNuxtApp()

const showExperimentalLayout = computed(() => {
  return $features?.getValue('new_desktop_imt_search_form') === 'on'
})

const countryFrom = ref<string>(props.defaultCountryFrom ?? '')
const countryTo = ref<string>(props.defaultCountryTo ?? '')
const currencyFrom = ref<string>(props.defaultCurrencyFrom ?? '')
const currencyTo = ref<string>(props.defaultCurrencyTo ?? '')
const amount = ref<number>(props.defaultAmount ?? 0)

onMounted(() => {
  if (!countryFrom.value) {
    countryFrom.value = 'CH'
  }

  if (!countryTo.value) {
    countryTo.value = getCountry(countryFrom.value)?.defaultCountryTo.iso ?? ''
  }

  if (!currencyFrom.value) {
    currencyFrom.value = getCountry(countryFrom.value)?.defaultCurrency.iso ?? ''
  }

  if (!currencyTo.value) {
    currencyTo.value = getCountry(countryTo.value)?.defaultCurrency.iso ?? ''
  }
})

const fromCurrencies = computed(() => {
  const curr = getCountry(countryFrom.value)?.currencies ?? []
  return curr.map(({ iso }) => ({ iso, label: getCurrencyLabel(iso) ?? iso }))
})

const toCurrencies = computed(() => {
  const curr = getCountry(countryTo.value)?.currencies ?? []
  return curr.map(({ iso }) => ({ iso, label: getCurrencyLabel(iso) ?? iso }))
})

function getCountry(country: string | null | undefined): Country | undefined {
  return $masterData.countries.get(country ?? '')
}

function getCurrencyLabel(iso: string): string | undefined {
  return $masterData.currencies.get(iso)?.labels.singular
}

function selectCountryFrom(country: string) {
  currencyFrom.value = getCountry(country)?.defaultCurrency.iso ?? ''
}

function selectCountryTo(country: string) {
  currencyTo.value = getCountry(country)?.defaultCurrency.iso ?? ''
}

const isLoading = ref(false)
async function submitHandler() {
  if (!countryFrom.value || !countryTo.value || !currencyFrom.value || !currencyTo.value) {
    return
  }
  const foundCountryFrom = getCountry(countryFrom.value)
  const foundCountryTo = getCountry(countryTo.value)
  if (!foundCountryFrom || !foundCountryTo) {
    return
  }
  isLoading.value = true
  await $track(
    SearchSubmit({
      type: 'Form',
      countryFrom: countryFrom.value,
      countryTo: countryTo.value,
      currencyFrom: currencyFrom.value,
      currencyTo: currencyTo.value,
      amount: amount.value,
    }),
  )
  const transferUrl = getIMTLiveUrl({
    countryFrom: { iso: foundCountryFrom.iso },
    countryTo: { iso: foundCountryTo.iso },
    currencyFrom: { iso: currencyFrom.value },
    currencyTo: { iso: currencyTo.value },
    amount: amount.value,
    payout: props.payout,
  })
  await navigate(transferUrl)
}
</script>

<style scoped>
.search-form:before {
  content: '';
  @apply absolute top-0 inset-x-0 -z-1 md:hidden bg-hero;
  height: 90%;
  clip-path: polygon(0 0, 100% 0, 100% calc(100% - 30px), 0% 100%);
}
</style>
